/* eslint-disable prefer-const */
import { VenueBasedInvoiceSerializer } from "@app-contexts/dashboard/shared/types/invoice";
import { PublicInvoiceDetailSerializer } from "@app-contexts/public/features/invoice-staging/data/types/invoice";
import { DataProvider } from "@shared/constants/common";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import isEqual from "lodash/isEqual";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import { recordPaymentError } from "./utils/errors/recordPaymentError";

import { CheckoutAddress, TicketBasket } from "../basket/types";
import PaymentHandler from "./handlers/PaymentHandler";
// eslint-disable-next-line import/no-cycle
import PaymentHandlerFactory from "./PaymentHandlerFactory";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "./services/useBasketPaymentService";
import useInvoicePaymentService from "./services/useInvoicePaymentService";
import { PurchaseOptsType } from "./types";
import { PaymentOrchestrationError } from "./utils/errors/PaymentOrchestrationError";
import { PaymentInitializationError } from "./utils/errors/PaymentInitializationError";
import { PaymentSystemError } from "./utils/errors/PaymentSystemError";
import type { HydrationState } from "./utils/errors/ErrorContextHydrator";
import { handlePaymentError } from "./utils/errors/handlePaymentError";
import {
  isPaymentOrchestrationError,
  getPaymentErrorMessage,
} from "./utils/errorUtils";
import { PaymentErrorCategories } from "./utils/errors/PaymentErrorCategories";
import { createPaymentLogger } from "./utils/payment-logger";

const log = createPaymentLogger("PaymentManager");

export type PaymentManagerConfig = {
  walletsEnabled: boolean;
  isLoggedIn?: boolean;
};

class PaymentManager {
  config: PaymentManagerConfig;
  basket: PaymentServiceBasket | undefined = undefined;
  handler: PaymentHandler | undefined = undefined;
  basketPaymentService: ReturnType<typeof useBasketPaymentService>;
  invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
  initializationPromise: Promise<unknown> | undefined = undefined;

  // --- External store API (for useSyncExternalStore) ---
  private stateVersion = 0;
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): number => {
    return this.stateVersion;
  };

  private emitChange(): void {
    this.stateVersion += 1;
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Whether the purchase flow has started.
   * This is used to prevent race conditions on basket & payment object updates.
   *
   * We perform payment object updates depending on certain variables (see `setBasket`). However these
   * setBasket calls could be caused by a basket update that started somewhere else (eg. updateAddresses ->
   * address on basket object changed -> call manager.setBasket).
   */
  purchaseFlowStarted = false;

  /**
   * Whether a purchase has completed successfully. Once true, `setBasket` will
   * no longer trigger `updatePaymentObject` — the manager is done and should
   * not reinitialize the gateway (which would create a needless Yuno session).
   */
  purchaseComplete = false;

  /**
   * Tracks recovery attempts to prevent infinite loops.
   * Maps error category to attempt count.
   */
  private recoveryAttempts = new Map<
    string,
    { count: number; lastAttempt: number }
  >();
  private static readonly MAX_RECOVERY_ATTEMPTS = 3;
  private static readonly RECOVERY_RESET_DELAY_MS = 60000; // Reset counter after 1 minute

  onPostPurchase:
    | ((purchaseData: {
        finalBasket: TicketBasket;
        invoice:
          | VenueBasedInvoiceSerializer
          | PublicInvoiceDetailSerializer
          | null;
      }) => void)
    | undefined = undefined;

  onProcessing: ((isProcessing: boolean) => void) | undefined = undefined;

  onPurchaseComplete: ((isPurchaseComplete: boolean) => void) | undefined =
    undefined;

  constructor({
    config,
    basketPaymentService,
    invoicePaymentService,
    onPostPurchase,
    onProcessing,
    onPurchaseComplete,
  }: {
    config: PaymentManagerConfig;
    basketPaymentService: ReturnType<typeof useBasketPaymentService>;
    invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    onPostPurchase?: (purchaseData: {
      finalBasket: TicketBasket;
      invoice:
        | VenueBasedInvoiceSerializer
        | PublicInvoiceDetailSerializer
        | null;
    }) => void;
    onProcessing?: (isProcessing: boolean) => void;
    onPurchaseComplete?: (isPurchaseComplete: boolean) => void;
  }) {
    this.config = config;
    this.basketPaymentService = basketPaymentService;
    this.invoicePaymentService = invoicePaymentService;
    this.onPostPurchase = onPostPurchase;
    this.onProcessing = onProcessing;
    this.onPurchaseComplete = onPurchaseComplete;
  }

  /**
   * Enhanced error handling for payment orchestration errors.
   * Hydrates error context at the catch point with full state information.
   * Separates user messages from technical details and attempts recovery when possible.
   */
  private handlePaymentError(
    error: unknown,
    context?: Record<string, unknown>
  ): never {
    const hydrationState: HydrationState = {
      basket: this.basket,
      basketType: this.basketPaymentService.basketType,
      handler: this.handler,
      attemptNumber: isPaymentOrchestrationError(error)
        ? this.recoveryAttempts.get(error.category)?.count
        : undefined,
    };

    handlePaymentError(error, hydrationState, context);

    if (isPaymentOrchestrationError(error) && error.recoverable) {
      this.attemptErrorRecovery(error)
        .then((recoverySuccessful) => {
          if (!recoverySuccessful) {
            log.info(
              "Error marked as non-recoverable — no recovery mechanism",
              {
                errorCategory: error.category,
              }
            );
          }
        })
        .catch((recoveryError) => {
          log.error("Error recovery promise failed", { recoveryError });
        });
    }

    throw error;
  }

  /**
   * Attempts to recover from recoverable errors with retry limiting.
   */
  private async attemptErrorRecovery(
    error: PaymentOrchestrationError
  ): Promise<boolean> {
    // Check and update retry count
    const now = Date.now();
    const attemptInfo = this.recoveryAttempts.get(error.category);

    // Reset counter if enough time has passed since last attempt
    if (
      attemptInfo &&
      now - attemptInfo.lastAttempt > PaymentManager.RECOVERY_RESET_DELAY_MS
    ) {
      this.recoveryAttempts.delete(error.category);
    }

    // Get current attempt count
    const currentAttempts =
      this.recoveryAttempts.get(error.category)?.count || 0;

    // Check if we've exceeded max attempts
    if (currentAttempts >= PaymentManager.MAX_RECOVERY_ATTEMPTS) {
      log.warn("Max recovery attempts exceeded", {
        errorCategory: error.category,
        attempts: currentAttempts,
        maxAttempts: PaymentManager.MAX_RECOVERY_ATTEMPTS,
      });
      return false; // Stop attempting recovery
    }

    // Update attempt tracking
    this.recoveryAttempts.set(error.category, {
      count: currentAttempts + 1,
      lastAttempt: now,
    });

    log.info("Attempting error recovery", {
      errorCategory: error.category,
      attemptNumber: currentAttempts + 1,
      maxAttempts: PaymentManager.MAX_RECOVERY_ATTEMPTS,
    });

    try {
      switch (error.category) {
        case PaymentErrorCategories.INITIALIZATION:
          log.info("Attempting recovery from initialization error", {
            attempt: currentAttempts + 1,
          });
          await this.attemptReinitialization();
          // Success - reset the counter for this category
          this.recoveryAttempts.delete(error.category);
          return true;

        default:
          log.info(`No recovery mechanism for category: ${error.category}`);
          return false; // No recovery mechanism available
      }
    } catch (recoveryError) {
      log.warn("Error recovery attempt failed", {
        errorCategory: error.category,
        attemptNumber: currentAttempts + 1,
        recoveryError,
      });
      recordPaymentError(recoveryError);
      return false; // Recovery failed
    }
  }

  /**
   * Resets recovery attempt counters after successful operations.
   * Call this after successful purchase, initialization, etc.
   */
  private resetRecoveryAttempts(): void {
    if (this.recoveryAttempts.size > 0) {
      log.info("Resetting recovery attempt counters", {
        categories: Array.from(this.recoveryAttempts.keys()),
      });
      this.recoveryAttempts.clear();
    }
  }

  /**
   * Attempts to reinitialize the payment manager and handler.
   */
  private async attemptReinitialization(): Promise<void> {
    if (!this.basket) {
      throw new PaymentInitializationError(
        "Cannot reinitialize without a basket"
      );
    }

    // Reset initialization state
    this.initializationPromise = undefined;
    if (this.handler) {
      this.handler.is_initialized = false;
    }

    // Attempt reinitialization
    await this.initialize(this.basket);
  }

  /**
   * Initializes this manager and the payment handler.
   * Must be called after the basket has been set on this Manager.
   * @param basket The basket to be used to perform initialization based on the basket's characteristics. @see {PaymentHandler} for more details.
   */
  async initialize(basket: PaymentServiceBasket) {
    log.info(`Initialize: Started for basket ${basket?.basketId}`);

    if (!this.handler) {
      throw new PaymentInitializationError(
        "Attempted to initialize the Payment Manager before a handler has been set."
      );
    }

    if (!this.handler.is_initialized && !this.initializationPromise) {
      this.initializationPromise = this.handler.initialize(basket);
    }

    try {
      await this.initializationPromise;
      // Reset recovery attempts after successful initialization
      this.resetRecoveryAttempts();
      this.emitChange();
    } catch (e) {
      // Clear the failed promise to allow retries on subsequent calls
      this.initializationPromise = undefined;

      log.error("Initialize: Error in initialization");
      // Propagate up
      throw this.handlePaymentError(e, {
        operation: "initialize",
      });
    }

    return this;
  }

  async updatePaymentObject(
    basket: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "basketUuid2" | "currency" | "totalPrice"
    >,
    confirmationStrategy?: PAYMENT_MANAGER_CONFIRMATION_STRATEGY
  ) {
    log.info("Update Payment Object: Started");

    if (!this.handler) {
      throw new PaymentInitializationError(
        "Attempted to update a payment object without a handler set."
      );
    }

    /**
     * Handlers with an undefined gateway means a gateway was not initiated.
     * @see {@link PaymentHandler.gateway} for more details.
     */
    if (this.handler.gateway === undefined) {
      throw new PaymentInitializationError(
        "Attempted to update a payment object without a gateway set."
      );
    }

    /**
     * Handlers with an explicit null gateway means a gateway is not needed by the handler.
     * @see {@link PaymentHandler.gateway} for more details.
     */
    if (this.handler.gateway === null) {
      return basket;
    }

    return this.handler.gateway.updatePaymentObject(basket, {
      confirmationStrategy,
    });
  }

  public setBasket(basket: PaymentServiceBasket): void {
    const prevBasket = this.basket;
    const prevHandler = this.handler;
    const prevGateway = prevHandler?.gateway;

    this.basket = basket;

    // Silent return, not a throw — setBasket is called reactively on every basket
    // change, including intermediate states where these fields aren't populated yet.
    if (!basket.basketId || !basket.paymentGateway) return;

    const shouldUpdatePaymentObject =
      !this.purchaseFlowStarted &&
      !this.purchaseComplete &&
      prevBasket !== undefined &&
      (!isEqual(prevBasket.totalPrice, basket.totalPrice) ||
        !isEqual(prevBasket.shippingAddress, basket.shippingAddress) ||
        !isEqual(prevBasket.billingAddress, basket.billingAddress) ||
        !isEqual(
          prevBasket.hasShippingAddressCollection,
          basket.hasShippingAddressCollection
        ));

    this.handler = PaymentHandlerFactory.getOrCreateHandler(
      this.config,
      this.basket,
      {
        basketPaymentService: this.basketPaymentService,
        invoicePaymentService: this.invoicePaymentService,
      }
    );

    if (!this.handler) return;

    const { gateway } = this.handler;

    if (gateway) {
      gateway.onProcessing = this.onProcessing;

      if (shouldUpdatePaymentObject && this.handler.is_initialized) {
        gateway.updatePaymentObject(basket).catch((e) => {
          recordPaymentError(e);
          NotifierFactory.createNotifier().error(getPaymentErrorMessage(e));
        });
      }
    }

    if (this.handler !== prevHandler || gateway !== prevGateway) {
      // When the handler changes (e.g. gateway switch after cart item changes),
      // the old initialization promise is stale — it resolved for a different
      // handler/gateway. Clear it so the context can trigger re-initialization.
      if (this.handler !== prevHandler) {
        this.initializationPromise = undefined;
      }

      this.emitChange();
    }
  }

  /**
   * Begins the purchasing flow.
   *
   * This goes through the prePurchase, processPurchase, postPurchase calls in full.
   *
   * @param confirmationStrategy Determines how the handlers and gateways should confirm this payment
   * @param purchaseOpts Flexible options to configure for this payment
   * @returns Post-purchase artifacts such as the invoice that was generated if immediately possible (ie. payment _capture_ is not asynchronous),
   * and the most recent basket shape as we capture the payment, deemed to be it's "final" shape.
   */
  public async purchase(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    this.purchaseFlowStarted = true;

    try {
      log.info("Purchase Call: Started");

      if (!this.basket) {
        throw new PaymentSystemError("No basket set for checkout.");
      }
      if (!this.handler) {
        throw new PaymentInitializationError(
          "No handler set for checkout. Either purchase was called before a handler was finished initializing, or an error happened in the initialization process."
        );
      }

      this.onProcessing?.(true);

      try {
        await this.prePurchase(confirmationStrategy, this.basket, purchaseOpts);

        await this.processPurchase(
          confirmationStrategy,
          this.basket,
          purchaseOpts
        );
        const postPurchaseResult = await this.postPurchase();

        this.onProcessing?.(false);
        return postPurchaseResult;
      } catch (e) {
        log.error("Purchase Call: Error in purchase");

        // Reset the status of the basket (ie. turning off purchase-in-progress locks)
        try {
          await this.basketPaymentService.reset();
        } catch (resetError) {
          log.error("Purchase Call: Error in resetting basket");
          recordPaymentError(resetError);
          NotifierFactory.createNotifier().error(
            getPaymentErrorMessage(resetError)
          );
        }

        try {
          await this.handler?.gateway?.renewCheckoutSession?.();
        } catch (renewError) {
          log.error("Failed to renew checkout session after payment error");
          recordPaymentError(renewError);
        }

        this.onProcessing?.(false);

        // Use enhanced error handling with context hydration
        return this.handlePaymentError(e);
      }
    } finally {
      this.purchaseFlowStarted = false;
    }
  }

  private async prepareAsyncHandlerConfirm({
    shouldPersist = true,
  }: {
    shouldPersist?: boolean;
  } = {}) {
    await this.basketPaymentService.extendBasket(true, {
      shouldPersist,
    });
  }

  /**
   * Handle pre-purchase logic if needed
   *
   * Basket updates are performed with `shouldPersist: false` here so that
   * all updates are batched to a single API call at the end with .save().
   * This mitigates issues with idempotency when `enable_payment_intent_lock` is on.
   */
  private async prePurchase(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    // Handle address updates
    await this.updateAddresses(purchaseOpts, {
      shouldPersist: false,
    });

    if (this.handler?.HAS_ASYNCHRONOUS_CONFIRMATION) {
      await this.prepareAsyncHandlerConfirm({
        shouldPersist: false,
      });
    }

    // explicitly persist the basket's fields just before proceeding with purchase confirmation
    await this.basketPaymentService.save();
    await this.updatePaymentObject(basket, confirmationStrategy);
  }

  /**
   * Update shipping and billing addresses based on purchase options
   * Handles special case for venue baskets where shipping address is used as billing address
   */
  private async updateAddresses(
    purchaseOpts: PurchaseOptsType,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) {
    let shouldUpdateBillingAddress = true;

    // Only update shipping when the caller explicitly provided a value.
    // undefined means "don't touch what's already on the basket."
    // Wallet flows (Yuno, Stripe) pass shipping_address: null to clear
    // the basket's existing address — the wallet confirmation SDK
    // (Apple Pay / Google Pay) captures shipping addresses itself.
    // Callers that own shipping (manual card entry, box office) pass null
    // to clear it or an address object to set it.
    if (purchaseOpts.shipping_address !== undefined) {
      await this.basketPaymentService.updateShippingAddress(
        (purchaseOpts.shipping_address as Required<CheckoutAddress>) ?? null,
        {
          shouldPersist,
        }
      );
    }

    // The box office does not ask for a billing_address, however the backend
    // fills in the basket.billing_address automatically based on
    // shipping_address. Mirror that here to keep client and server in sync.
    if (
      this.basketPaymentService.basketType === DataProvider.Venue &&
      purchaseOpts.shipping_address !== undefined
    ) {
      await this.basketPaymentService.updateBillingAddress(
        purchaseOpts.shipping_address as Required<CheckoutAddress>,
        {
          shouldPersist,
        }
      );
      shouldUpdateBillingAddress = false;
    }

    if (
      shouldUpdateBillingAddress &&
      purchaseOpts.billing_address !== undefined
    ) {
      await this.basketPaymentService.updateBillingAddress(
        (purchaseOpts.billing_address as Required<CheckoutAddress>) ?? null,
        {
          shouldPersist,
        }
      );
    }
  }

  /**
   * Handle the purchase itself. This should involve a purchase call on behalf of the basket
   * endpoint and/or the handler's processing logic.
   */
  private async processPurchase(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info("Process Purchase: Started");

    if (!this.handler) {
      throw new PaymentInitializationError(
        "No handler set for checkout. Either purchase was called before a handler was finished initializing, or an error happened in the initialization process."
      );
    }

    if (basket.basketId == null) {
      throw new PaymentSystemError("Invalid basket id to purchase");
    }

    // Determine purchase options
    const newPurchaseOpts: typeof purchaseOpts = {};
    if (basket.isGroupSale) {
      newPurchaseOpts.is_group_sale = true;
      newPurchaseOpts.customer_group = purchaseOpts.customer_group;
    }

    if (purchaseOpts.exchange_invoice_transaction_id) {
      newPurchaseOpts.exchange_invoice_transaction_id =
        purchaseOpts.exchange_invoice_transaction_id;
    }

    if (purchaseOpts.billing_address) {
      newPurchaseOpts.billing_address = purchaseOpts.billing_address;
    }

    if (
      purchaseOpts.shipping_address &&
      this.basketPaymentService.basket.hasShippingAddressCollection
    ) {
      newPurchaseOpts.shipping_address = purchaseOpts.shipping_address;
    }

    if (purchaseOpts.name_on_card) {
      newPurchaseOpts.name_on_card = purchaseOpts.name_on_card;
    }

    if (purchaseOpts.wallet_one_time_token) {
      newPurchaseOpts.wallet_one_time_token =
        purchaseOpts.wallet_one_time_token;
    }

    return this.handler.processCheckout(
      confirmationStrategy,
      this.basketPaymentService.basketType,
      basket,
      newPurchaseOpts
    );
  }

  public async handleThirdPartyRedirect() {
    this.purchaseFlowStarted = true;
    this.onProcessing?.(true);

    log.info("Handle Third Party Redirect: Initializing");

    // initialize the handler
    this.basket = this.basketPaymentService.basket;
    this.setBasket(this.basket);

    try {
      return await this.postPurchase();
    } catch (e) {
      log.error("Handle Third Party Redirect: Error in postPurchase");

      NotifierFactory.createNotifier().error(
        isPaymentOrchestrationError(e)
          ? e.userMessage
          : "Something went wrong with your payment. Please try again."
      );

      recordPaymentError(e);

      try {
        await this.basketPaymentService.reset();
      } catch (e) {
        log.error("Handle Third Party Redirect: Error in resetting basket");
        recordPaymentError(e);
        NotifierFactory.createNotifier().error(getPaymentErrorMessage(e));
      }

      this.onProcessing?.(false);

      throw e;
    } finally {
      this.purchaseFlowStarted = false;
    }
  }

  public async getInvoice(finalBasket: TicketBasket) {
    log.info("Get Invoice: Started");

    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */
    /* eslint-disable @typescript-eslint/no-non-null-assertion */

    // Now, poll for the invoice using the invoice_id we obtained
    const MAX_INVOICE_ATTEMPTS = 5;
    let invoice = null;

    for (let attempts = 1; attempts <= MAX_INVOICE_ATTEMPTS; attempts++) {
      const invoiceId = finalBasket.invoice_id;

      try {
        // Use user-based endpoint if user is logged in and has user_id
        // Otherwise use public endpoint with UUIDs for guest checkout
        invoice =
          finalBasket.user_id && this.config.isLoggedIn
            ? await this.invoicePaymentService.retrieve(invoiceId.toString())
            : await this.invoicePaymentService.retrieve(invoiceId.toString(), {
                uuid: finalBasket.uuid!,
                uuid2: finalBasket.uuid2!,
              });

        // If we got an invoice, break out of the loop
        if (invoice) break;
      } catch (error) {
        if (attempts === MAX_INVOICE_ATTEMPTS) {
          log.warn(`Could not get invoice after ${attempts} attempts`);
          // Just continue with invoice as null
          return null;
        }
      }

      // Wait before trying again if we haven't found an invoice yet
      if (attempts < MAX_INVOICE_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    return invoice;
  }

  /**
   * Handle post-purchase logic if needed.
   */
  private async postPurchase() {
    log.info("Post purchase started");

    if (!this.handler) {
      throw new PaymentInitializationError(
        "Attempted to post process a purchase without a handler set."
      );
    }

    const finalBasket = await this.handler.postProcessCheckout();

    let invoice:
      | VenueBasedInvoiceSerializer
      | PublicInvoiceDetailSerializer
      | null = null;
    // Waitlisted baskets do not generate an invoice on payment method capture,
    // so for those that are not waitlisted, fetch the invoice.
    // This logic lives here as in 90% of PHandler + PGateway combinations, we will need to fetch the invoice.
    // Should that ratio shift, this logic can move elsewhere if needed.
    if (!finalBasket.is_waitlist) {
      invoice = await this.getInvoice(finalBasket);
    }

    this.onPostPurchase?.({ finalBasket, invoice });

    // Reset recovery attempts after successful purchase
    this.resetRecoveryAttempts();

    this.purchaseComplete = true;
    this.onPurchaseComplete?.(true);
    await this.basketPaymentService.emptyBasket();

    if (this.handler) {
      // TODO need to add into whole reset function and reset flow across the payment manager objects
      this.handler.is_initialized = false;
    }
    return { invoice, finalBasket };
  }

  /**
   * Updates this object's references to the services
   */
  public setServices(
    services: Omit<ConstructorParameters<typeof PaymentManager>[0], "config">
  ) {
    this.basketPaymentService = services.basketPaymentService;
    this.invoicePaymentService = services.invoicePaymentService;

    // Propagate service updates to the handler
    if (this.handler?.setServices) {
      this.handler.setServices({
        basketPaymentService: services.basketPaymentService,
        invoicePaymentService: services.invoicePaymentService,
      });
    }

    this.emitChange();
  }
}

export default PaymentManager;
